import { useNavigate, useParams } from "react-router-dom";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { confirmDialog } from "primereact/confirmdialog";

import { EmploymentStatusFormRule, EmploymentStatusFormSchema } from "@core/form/form.rule";
import { useFormDefaultEmploymentStatus } from "@core/form/form-default-employment-status.hook";
import { useFormApiMapper } from "@core/services/form-api-mapper.hook";
import { ActionEnum } from "@core/model/actions.enum";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { 
  useQyUpdateEmploymentStatus, 
  useQyGetEmploymentStatusById, 
  useQyDeleteEmploymentStatus 
} from "@core/query/employment-status.query";
import { GetResponseEmploymentStatusModel } from "@core/model/query-model-employment-status";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";

export function useEditEmploymentStatus() {
  // LOCAL DECLARATION
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultEmploymentStatus();
  const navigate = useNavigate();
  const { mapUpdateEmploymentStatus, mapDeleteEmploymentStatus } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { employmentStatusId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID EmploymentStatus
  const handleGetApiSuccess = (data: GetResponseEmploymentStatusModel) => {
    if (data && data.count && data.count > 0) {
      const responseData = data.data?.[0];
      setValue("name", responseData?.name || "");
      setValue("description", responseData?.description || "");
    }
  };
  const {
    data: employmentStatusResponse,
    isLoading,
    isError: employmentStatusError,
  } = useQyGetEmploymentStatusById(employmentStatusId || "", handleGetApiSuccess);
  const employmentStatusData = employmentStatusResponse?.data?.[0];

  // API UPDATE EmploymentStatus
  const handleUpdateApiSuccess = () => {
    showSuccess("EmploymentStatus updated successfully");
  };
  const { mutate: updateEmploymentStatus, isLoading: isEditLoading } = useQyUpdateEmploymentStatus(
    handleUpdateApiSuccess
  );

  // API DELETE EmploymentStatus
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("EmploymentStatus deleted successfully");
  };
  const { mutate: deleteEmploymentStatus } = useQyDeleteEmploymentStatus(
    handleDeleteApiSuccess
  );

  // LOCAL FUNCTIONS
  const formMethod = useForm<EmploymentStatusFormSchema>({
    defaultValues: getDefault(employmentStatusData),
    resolver: zodResolver(EmploymentStatusFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: EmploymentStatusFormSchema) => {
    if (!employmentStatusData) {
      throw new Error("No employmentStatus data");
    }

    const formData = mapUpdateEmploymentStatus(form, employmentStatusData.code);
    updateEmploymentStatus(formData);
  };
  const handleValidateError = (err: FieldErrors<EmploymentStatusFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!employmentStatusData) {
      throw new Error("No employmentStatus data found");
    }

    const form = mapDeleteEmploymentStatus(employmentStatusData.code);
    deleteEmploymentStatus(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;
      
      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

   return {
    employmentStatusId,
    employmentStatusData,
    isLoading,
    employmentStatusError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}